// The sender's side of a screen share connection, summarized for a display that has stats logging turned on. The display
// asks with each of its own samples (sender-stats-request) and logs the answer next to its own stats.

type StatsEntry = Record<string, unknown>;

export interface SenderStatsSnapshot {
  video?: StatsEntry;
  source?: StatsEntry;
  remoteInbound?: StatsEntry;
  candidatePair?: StatsEntry;
  localCandidate?: StatsEntry;
  remoteCandidate?: StatsEntry;
}

function numberOf(entry: unknown, key: string): number | undefined {
  const value = (entry as StatsEntry | undefined)?.[key];
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

function round(value: number | undefined, decimals = 0): number | undefined {
  if (value === undefined || !Number.isFinite(value)) {
    return undefined;
  }
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Change of a cumulative counter since the previous sample; since the connection started for the first one. */
function deltaOf(
  current: unknown,
  previous: unknown,
  key: string,
): number | undefined {
  const now = numberOf(current, key);
  if (now === undefined) {
    return undefined;
  }
  return now - (numberOf(previous, key) ?? 0);
}

/** Average per item over the interval, in ms, of a cumulative total in seconds, e.g. encode time per frame. */
function averageMs(
  current: StatsEntry | undefined,
  previous: StatsEntry | undefined,
  totalKey: string,
  countKey: string,
): number | undefined {
  const total = deltaOf(current, previous, totalKey);
  const count = deltaOf(current, previous, countKey);
  return total !== undefined && count
    ? round((total / count) * 1000, 1)
    : undefined;
}

function secondsToMs(value: number | undefined): number | undefined {
  return value === undefined ? undefined : round(value * 1000, 1);
}

function candidateOf(entry: StatsEntry | undefined): string | undefined {
  if (!entry) {
    return undefined;
  }
  return [entry.candidateType, entry.protocol, entry.relayProtocol]
    .filter(Boolean)
    .join('/');
}

export function senderSnapshotOf(report: RTCStatsReport): SenderStatsSnapshot {
  const snapshot: SenderStatsSnapshot = {};
  const byId = new Map<string, StatsEntry>();
  let selectedPairId: string | undefined;
  let fallbackPair: StatsEntry | undefined;

  report.forEach((value) => {
    const stat = value as StatsEntry;
    byId.set(stat.id as string, stat);
    if (stat.type === 'outbound-rtp' && stat.kind === 'video') {
      snapshot.video = stat;
    } else if (stat.type === 'media-source' && stat.kind === 'video') {
      snapshot.source = stat;
    } else if (stat.type === 'remote-inbound-rtp' && stat.kind === 'video') {
      snapshot.remoteInbound = stat;
    } else if (
      stat.type === 'transport' &&
      typeof stat.selectedCandidatePairId === 'string'
    ) {
      selectedPairId = stat.selectedCandidatePairId;
    } else if (
      stat.type === 'candidate-pair' &&
      stat.nominated &&
      stat.state === 'succeeded'
    ) {
      fallbackPair = stat;
    }
  });

  snapshot.candidatePair =
    (selectedPairId && byId.get(selectedPairId)) || fallbackPair;
  snapshot.localCandidate = byId.get(
    snapshot.candidatePair?.localCandidateId as string,
  );
  snapshot.remoteCandidate = byId.get(
    snapshot.candidatePair?.remoteCandidateId as string,
  );
  return snapshot;
}

/** The send side over one interval: what the capture produced, how the encoder and pacer kept up, and the network. */
export function summarizeSenderStats(
  current: SenderStatsSnapshot,
  previous: SenderStatsSnapshot | null,
): Record<string, unknown> {
  const { video, source, remoteInbound, candidatePair } = current;
  const previousVideo = previous?.video;
  const elapsedMs = previousVideo
    ? deltaOf(video, previousVideo, 'timestamp')
    : undefined;
  const bytes = deltaOf(video, previousVideo, 'bytesSent');
  const width = numberOf(video, 'frameWidth');
  const height = numberOf(video, 'frameHeight');
  const targetBitrate = numberOf(video, 'targetBitrate');
  const limitations = video?.qualityLimitationDurations;
  const previousLimitations = previousVideo?.qualityLimitationDurations;
  const outgoingBitrate = numberOf(candidatePair, 'availableOutgoingBitrate');

  return {
    intervalS: round(elapsedMs !== undefined ? elapsedMs / 1000 : undefined, 1),
    video: video && {
      size: width && height ? `${width}x${height}` : undefined,
      fps: round(numberOf(video, 'framesPerSecond'), 1),
      // Frames coming out of the capture pipeline, before the encoder.
      sourceFps: round(numberOf(source, 'framesPerSecond'), 1),
      kbps:
        bytes !== undefined && elapsedMs
          ? round((bytes * 8) / elapsedMs)
          : undefined,
      targetKbps: round(
        targetBitrate !== undefined ? targetBitrate / 1000 : undefined,
      ),
      encoded: deltaOf(video, previousVideo, 'framesEncoded'),
      sent: deltaOf(video, previousVideo, 'framesSent'),
      keyframes: deltaOf(video, previousVideo, 'keyFramesEncoded'),
      encodeMs: averageMs(
        video,
        previousVideo,
        'totalEncodeTime',
        'framesEncoded',
      ),
      // Time packets waited in the pacer before going out.
      pacerMs: averageMs(
        video,
        previousVideo,
        'totalPacketSendDelay',
        'packetsSent',
      ),
      retransmitted: deltaOf(video, previousVideo, 'retransmittedPacketsSent'),
      nacks: deltaOf(video, previousVideo, 'nackCount'),
      plis: deltaOf(video, previousVideo, 'pliCount'),
      limitedBy: video.qualityLimitationReason,
      cpuLimitedS: round(deltaOf(limitations, previousLimitations, 'cpu'), 1),
      bandwidthLimitedS: round(
        deltaOf(limitations, previousLimitations, 'bandwidth'),
        1,
      ),
      encoder: video.encoderImplementation,
      powerEfficient: video.powerEfficientEncoder,
    },
    // The display's receiver reports, as seen by the sender.
    remote: remoteInbound && {
      rttMs: secondsToMs(numberOf(remoteInbound, 'roundTripTime')),
      jitterMs: secondsToMs(numberOf(remoteInbound, 'jitter')),
      fractionLost: round(numberOf(remoteInbound, 'fractionLost'), 3),
    },
    connection: candidatePair && {
      rttMs: secondsToMs(numberOf(candidatePair, 'currentRoundTripTime')),
      availableKbps: round(
        outgoingBitrate !== undefined ? outgoingBitrate / 1000 : undefined,
      ),
      local: candidateOf(current.localCandidate),
      remote: candidateOf(current.remoteCandidate),
    },
  };
}
